import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { Button } from '../ui/button'
import { useFAQManagement } from '../../hooks/useFAQ'
import { showErrorToast } from '../../utils/toast'

export default function FAQModal({ isOpen, onClose, onSuccess, faq, categories = [] }) {
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: '',
    order: 0,
    isPublished: false
  })
  const [errors, setErrors] = useState({})
  const { createFAQ, updateFAQ, loading } = useFAQManagement()

  useEffect(() => {
    if (faq) {
      setFormData({
        question: faq.question || '',
        answer: faq.answer || '',
        category: faq.category || '',
        order: faq.order || 0,
        isPublished: faq.isPublished || false
      })
    } else {
      setFormData({
        question: '',
        answer: '',
        category: '',
        order: 0,
        isPublished: false
      })
    }
    setErrors({})
  }, [faq, isOpen])

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validate = () => {
    const newErrors = {}
    
    if (!formData.question.trim()) {
      newErrors.question = 'La question est requise'
    } else if (formData.question.length > 500) {
      newErrors.question = 'La question ne peut pas dépasser 500 caractères'
    }

    if (!formData.answer.trim()) {
      newErrors.answer = 'La réponse est requise'
    } else if (formData.answer.length > 5000) {
      newErrors.answer = 'La réponse ne peut pas dépasser 5000 caractères'
    }

    if (formData.order < 0) {
      newErrors.order = 'L\'ordre doit être positif ou nul'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    const faqData = {
      question: formData.question.trim(),
      answer: formData.answer.trim(),
      ...(formData.category && { category: formData.category }),
      ...(formData.order !== undefined && { order: parseInt(formData.order) }),
      ...(formData.isPublished !== undefined && { isPublished: formData.isPublished })
    }

    const result = faq
      ? await updateFAQ(faq.id, faqData)
      : await createFAQ(faqData)

    if (result.success) {
      onSuccess()
    } else {
      showErrorToast({ message: result.error || 'Erreur lors de l\'enregistrement' })
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            {faq ? 'Modifier la FAQ' : 'Créer une nouvelle FAQ'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Question */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Question <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.question}
              onChange={(e) => handleChange('question', e.target.value)}
              rows={2}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                errors.question ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Entrez la question..."
            />
            {errors.question && (
              <p className="mt-1 text-sm text-red-600">{errors.question}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {formData.question.length}/500 caractères
            </p>
          </div>

          {/* Answer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Réponse <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.answer}
              onChange={(e) => handleChange('answer', e.target.value)}
              rows={6}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                errors.answer ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Entrez la réponse..."
            />
            {errors.answer && (
              <p className="mt-1 text-sm text-red-600">{errors.answer}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {formData.answer.length}/5000 caractères
            </p>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Catégorie
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent"
            >
              <option value="">Sélectionner une catégorie</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Order */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ordre d'affichage
            </label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
              min="0"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                errors.order ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.order && (
              <p className="mt-1 text-sm text-red-600">{errors.order}</p>
            )}
          </div>

          {/* Published */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isPublished"
              checked={formData.isPublished}
              onChange={(e) => handleChange('isPublished', e.target.checked)}
              className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
            />
            <label htmlFor="isPublished" className="ml-2 text-sm font-medium text-gray-700">
              Publier cette FAQ
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
              disabled={loading}
            >
              {loading ? 'Enregistrement...' : (faq ? 'Mettre à jour' : 'Créer')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
